import React from 'react';
import styled from 'styled-components';
import { useConsent } from '../../context/ConsentContext';
import { PrimaryButton, SecondaryButton, OutlineButton } from '../common/Button';

const Wrapper = styled.div`
    position: fixed;
    left: 16px;
    right: 16px;
    bottom: 16px;
    z-index: 10000005;
    background: ${(p) => p.theme.colors.background};
    color: ${(p) => p.theme.colors.text};
    border: 1px solid ${(p) => p.theme.colors.border};
    border-radius: 12px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
    backdrop-filter: blur(20px) saturate(180%);
    -webkit-backdrop-filter: blur(20px) saturate(180%);
    padding: 20px 22px;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 16px;
    align-items: center;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const Body = styled.div`
    h3 {
        margin: 0 0 6px;
        font-size: 16px;
        font-weight: 700;
    }
    p {
        margin: 0;
        font-size: 14px;
        line-height: 1.5;
        opacity: 0.85;
    }
`;

const Actions = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-end;

    @media (max-width: 720px) {
        justify-content: stretch;
        & > button {
            flex: 1;
        }
    }
`;

function defaultCopy(policy, isEU) {
    const fallback = {
        bannerTitle: 'We value your privacy',
        bannerBody: isEU
            ? 'We use strictly necessary cookies by default. With your permission we will also use analytics, marketing and personalization cookies. You can change your choice anytime from the footer.'
            : 'We use cookies to keep the site running and, with your permission, to measure usage and personalize your experience. You can change your choice anytime from the footer.',
        acceptAll: 'Accept all',
        rejectAll: 'Reject all',
        managePreferences: 'Manage preferences',
    };
    const copy = policy?.copy?.en || {};
    return { ...fallback, ...copy };
}

export default function ConsentBanner() {
    const { needsBanner, policy, isEU, acceptAll, rejectAll, openPreferences } = useConsent();
    if (!needsBanner) return null;

    const copy = defaultCopy(policy, isEU);

    return (
        <Wrapper role="dialog" aria-live="polite" aria-label="Cookie consent">
            <Body>
                <h3>{copy.bannerTitle}</h3>
                <p>{copy.bannerBody}</p>
            </Body>
            <Actions>
                <OutlineButton type="button" onClick={openPreferences}>
                    {copy.managePreferences}
                </OutlineButton>
                <SecondaryButton type="button" onClick={() => rejectAll()}>
                    {copy.rejectAll}
                </SecondaryButton>
                <PrimaryButton type="button" onClick={() => acceptAll()}>
                    {copy.acceptAll}
                </PrimaryButton>
            </Actions>
        </Wrapper>
    );
}
